// IndexedDB storage for offline PDV operations

const DB_NAME = 'actualfood-offline';
const DB_VERSION = 2; // Bumped version for new stores
const PENDING_ORDERS_STORE = 'pending_orders';
const CACHED_DATA_STORE = 'cached_data';
const ACTIVE_CONSUMPTIONS_STORE = 'active_consumptions';
const PENDING_ITEMS_STORE = 'pending_items';
const PENDING_CLOSURES_STORE = 'pending_closures';

// Timeout for IndexedDB operations to prevent hanging
const DB_OPEN_TIMEOUT = 3000; // 3 seconds

// ============ Type Definitions ============

export interface PendingOrder {
  id: string;
  restaurantId: string;
  items: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    notes?: string;
  }>;
  orderInfo: {
    type: 'table' | 'tab' | 'counter' | 'delivery';
    tableNumber?: string;
    tabNumber?: string;
    customerName?: string;
    notes?: string;
  };
  discount: number;
  payments: Array<{
    paymentMethodId: string;
    paymentMethodName: string;
    amount: number;
    feeAmount: number;
  }>;
  serviceTax: number;
  couvertQuantity: number;
  couvertAmount: number;
  tableId?: string;
  commandId?: string;
  subtotal: number;
  total: number;
  createdAt: string;
  syncStatus: 'pending' | 'syncing' | 'failed';
  syncAttempts: number;
  lastSyncError?: string;
}

export interface ActiveConsumption {
  id: string; // tableId or commandId
  type: 'table' | 'command';
  restaurantId: string;
  orderId: string;
  orderNumber: number;
  number: string; // table or command number
  customerName?: string;
  items: Array<{
    id: string;
    productId?: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    notes?: string;
  }>;
  subtotal: number;
  total: number;
  serviceTaxEnabled?: boolean;
  updatedAt: string;
  syncedAt: string;
}

export interface PendingItem {
  id: string;
  restaurantId: string;
  tableId?: string;
  commandId?: string;
  orderId?: string; // From cached active consumption
  orderNumber?: number;
  type: 'table' | 'tab';
  number: string; // table or command number
  items: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    notes?: string;
    comboId?: string;
    comboSelections?: any;
  }>;
  waiterName?: string;
  createdAt: string;
  syncStatus: 'pending' | 'syncing' | 'failed';
  syncAttempts: number;
  lastSyncError?: string;
}

export interface PendingClosure {
  id: string;
  restaurantId: string;
  orderId: string;
  orderNumber: number;
  tableId?: string;
  commandId?: string;
  type: 'table' | 'tab' | 'counter';
  number: string;
  customerName?: string;
  subtotal: number;
  discount: number;
  serviceTax: number;
  couvertQuantity: number;
  couvertAmount: number;
  total: number;
  payments: Array<{
    paymentMethodId: string;
    paymentMethodName: string;
    amount: number;
    feeAmount: number;
  }>;
  peopleCount?: number;
  createdAt: string;
  syncStatus: 'pending' | 'syncing' | 'failed';
  syncAttempts: number;
  lastSyncError?: string;
}

interface CachedData {
  key: string;
  data: unknown;
  timestamp: number;
}

// ============ Database Connection ============

let db: IDBDatabase | null = null;
let dbOpenPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  // Return existing connection if available
  if (db) {
    return Promise.resolve(db);
  }

  // Return existing promise if we're already opening
  if (dbOpenPromise) {
    return dbOpenPromise;
  }

  dbOpenPromise = new Promise((resolve, reject) => {
    // Timeout to prevent indefinite hanging
    const timeoutId = setTimeout(() => {
      dbOpenPromise = null;
      reject(new Error('IndexedDB open timeout - feche outras abas e tente novamente'));
    }, DB_OPEN_TIMEOUT);

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        clearTimeout(timeoutId);
        dbOpenPromise = null;
        reject(new Error('Failed to open IndexedDB'));
      };

      // Handle blocked event (another tab has the DB open with an older version)
      request.onblocked = () => {
        clearTimeout(timeoutId);
        dbOpenPromise = null;
        reject(new Error('IndexedDB blocked - feche outras abas do sistema'));
      };

      request.onsuccess = () => {
        clearTimeout(timeoutId);
        db = request.result;
        
        // Handle version change (when another tab upgrades the DB)
        db.onversionchange = () => {
          db?.close();
          db = null;
          dbOpenPromise = null;
        };
        
        // Handle unexpected close
        db.onclose = () => {
          db = null;
          dbOpenPromise = null;
        };
        
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const database = (event.target as IDBOpenDBRequest).result;

        // Pending orders store (existing)
        if (!database.objectStoreNames.contains(PENDING_ORDERS_STORE)) {
          const ordersStore = database.createObjectStore(PENDING_ORDERS_STORE, { keyPath: 'id' });
          ordersStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          ordersStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Cached data store (existing)
        if (!database.objectStoreNames.contains(CACHED_DATA_STORE)) {
          database.createObjectStore(CACHED_DATA_STORE, { keyPath: 'key' });
        }

        // NEW: Active consumptions store (for offline table/command viewing)
        if (!database.objectStoreNames.contains(ACTIVE_CONSUMPTIONS_STORE)) {
          const consumptionsStore = database.createObjectStore(ACTIVE_CONSUMPTIONS_STORE, { keyPath: 'id' });
          consumptionsStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          consumptionsStore.createIndex('type', 'type', { unique: false });
          consumptionsStore.createIndex('orderId', 'orderId', { unique: false });
        }

        // NEW: Pending items store (for offline item additions)
        if (!database.objectStoreNames.contains(PENDING_ITEMS_STORE)) {
          const itemsStore = database.createObjectStore(PENDING_ITEMS_STORE, { keyPath: 'id' });
          itemsStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          itemsStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          itemsStore.createIndex('orderId', 'orderId', { unique: false });
        }

        // NEW: Pending closures store (for offline checkout)
        if (!database.objectStoreNames.contains(PENDING_CLOSURES_STORE)) {
          const closuresStore = database.createObjectStore(PENDING_CLOSURES_STORE, { keyPath: 'id' });
          closuresStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          closuresStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          closuresStore.createIndex('orderId', 'orderId', { unique: false });
        }
      };
    } catch (error) {
      clearTimeout(timeoutId);
      dbOpenPromise = null;
      reject(new Error('IndexedDB not available'));
    }
  });

  return dbOpenPromise;
}

// ============ Pending Orders Functions (Existing) ============

export async function savePendingOrder(order: Omit<PendingOrder, 'id' | 'createdAt' | 'syncStatus' | 'syncAttempts'>): Promise<string> {
  const database = await openDatabase();
  
  const pendingOrder: PendingOrder = {
    ...order,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
    syncAttempts: 0,
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ORDERS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ORDERS_STORE);
    const request = store.add(pendingOrder);

    request.onerror = () => reject(new Error('Failed to save pending order'));
    request.onsuccess = () => resolve(pendingOrder.id);
  });
}

export async function getPendingOrders(restaurantId?: string): Promise<PendingOrder[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ORDERS_STORE, 'readonly');
    const store = transaction.objectStore(PENDING_ORDERS_STORE);
    const request = store.getAll();

    request.onerror = () => reject(new Error('Failed to get pending orders'));
    request.onsuccess = () => {
      let orders = request.result as PendingOrder[];
      if (restaurantId) {
        orders = orders.filter(o => o.restaurantId === restaurantId);
      }
      // Sort by createdAt ascending (oldest first for sync)
      orders.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      resolve(orders);
    };
  });
}

export async function updatePendingOrderStatus(
  id: string, 
  status: PendingOrder['syncStatus'], 
  error?: string
): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ORDERS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ORDERS_STORE);
    const getRequest = store.get(id);

    getRequest.onerror = () => reject(new Error('Failed to get pending order'));
    getRequest.onsuccess = () => {
      const order = getRequest.result as PendingOrder;
      if (!order) {
        reject(new Error('Order not found'));
        return;
      }

      order.syncStatus = status;
      order.syncAttempts += 1;
      if (error) {
        order.lastSyncError = error;
      }

      const putRequest = store.put(order);
      putRequest.onerror = () => reject(new Error('Failed to update pending order'));
      putRequest.onsuccess = () => resolve();
    };
  });
}

export async function deletePendingOrder(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ORDERS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ORDERS_STORE);
    const request = store.delete(id);

    request.onerror = () => reject(new Error('Failed to delete pending order'));
    request.onsuccess = () => resolve();
  });
}

export async function getPendingOrderCount(restaurantId: string): Promise<number> {
  const orders = await getPendingOrders(restaurantId);
  return orders.filter(o => o.syncStatus !== 'syncing').length;
}

// Check if there are any pending offline operations (orders or items)
// targeting a specific table/command. Used to block phantom-clears in
// `useCurrentOrderItems` when the server reports the table as available
// but offline work is still queued locally.
export async function hasPendingOpsForEntity(
  restaurantId: string,
  opts: { tableId?: string; commandId?: string; tableNumber?: string; tabNumber?: string }
): Promise<boolean> {
  try {
    const [orders, items] = await Promise.all([
      getPendingOrders(restaurantId),
      getPendingItems(restaurantId),
    ]);

    const tableNum = opts.tableNumber ? String(opts.tableNumber) : undefined;
    const tabNum = opts.tabNumber ? String(opts.tabNumber) : undefined;

    const matchOrder = (o: PendingOrder) => {
      if (opts.tableId && o.tableId === opts.tableId) return true;
      if (opts.commandId && o.commandId === opts.commandId) return true;
      if (tableNum && o.orderInfo?.tableNumber && String(o.orderInfo.tableNumber) === tableNum) return true;
      if (tabNum && o.orderInfo?.tabNumber && String(o.orderInfo.tabNumber) === tabNum) return true;
      return false;
    };

    const matchItem = (i: PendingItem) => {
      if (opts.tableId && i.tableId === opts.tableId) return true;
      if (opts.commandId && i.commandId === opts.commandId) return true;
      if (tableNum && i.number && String(i.number) === tableNum && i.type === 'table') return true;
      if (tabNum && i.number && String(i.number) === tabNum && i.type === 'tab') return true;
      return false;
    };

    return orders.some(matchOrder) || items.some(matchItem);
  } catch (err) {
    console.warn('[offline-storage] hasPendingOpsForEntity failed:', err);
    return false;
  }
}

// ============ Active Consumptions Functions (NEW) ============

export async function saveActiveConsumption(consumption: ActiveConsumption): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(ACTIVE_CONSUMPTIONS_STORE, 'readwrite');
    const store = transaction.objectStore(ACTIVE_CONSUMPTIONS_STORE);
    const request = store.put(consumption);

    request.onerror = () => reject(new Error('Failed to save active consumption'));
    request.onsuccess = () => resolve();
  });
}

export async function getActiveConsumption(id: string): Promise<ActiveConsumption | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(ACTIVE_CONSUMPTIONS_STORE, 'readonly');
    const store = transaction.objectStore(ACTIVE_CONSUMPTIONS_STORE);
    const request = store.get(id);

    request.onerror = () => reject(new Error('Failed to get active consumption'));
    request.onsuccess = () => {
      resolve(request.result || null);
    };
  });
}

export async function getActiveConsumptionByOrderId(orderId: string): Promise<ActiveConsumption | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(ACTIVE_CONSUMPTIONS_STORE, 'readonly');
    const store = transaction.objectStore(ACTIVE_CONSUMPTIONS_STORE);
    const index = store.index('orderId');
    const request = index.get(orderId);

    request.onerror = () => reject(new Error('Failed to get active consumption by orderId'));
    request.onsuccess = () => {
      resolve(request.result || null);
    };
  });
}

export async function getActiveConsumptions(restaurantId: string): Promise<ActiveConsumption[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(ACTIVE_CONSUMPTIONS_STORE, 'readonly');
    const store = transaction.objectStore(ACTIVE_CONSUMPTIONS_STORE);
    const request = store.getAll();

    request.onerror = () => reject(new Error('Failed to get active consumptions'));
    request.onsuccess = () => {
      const consumptions = (request.result as ActiveConsumption[])
        .filter(c => c.restaurantId === restaurantId);
      resolve(consumptions);
    };
  });
}

export async function deleteActiveConsumption(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(ACTIVE_CONSUMPTIONS_STORE, 'readwrite');
    const store = transaction.objectStore(ACTIVE_CONSUMPTIONS_STORE);
    const request = store.delete(id);

    request.onerror = () => reject(new Error('Failed to delete active consumption'));
    request.onsuccess = () => resolve();
  });
}

// ============ Pending Items Functions (NEW) ============

export async function savePendingItem(item: Omit<PendingItem, 'id' | 'createdAt' | 'syncStatus' | 'syncAttempts'>): Promise<string> {
  const database = await openDatabase();
  
  const pendingItem: PendingItem = {
    ...item,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
    syncAttempts: 0,
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ITEMS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ITEMS_STORE);
    const request = store.add(pendingItem);

    request.onerror = () => reject(new Error('Failed to save pending item'));
    request.onsuccess = () => resolve(pendingItem.id);
  });
}

export async function getPendingItems(restaurantId?: string): Promise<PendingItem[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ITEMS_STORE, 'readonly');
    const store = transaction.objectStore(PENDING_ITEMS_STORE);
    const request = store.getAll();

    request.onerror = () => reject(new Error('Failed to get pending items'));
    request.onsuccess = () => {
      let items = request.result as PendingItem[];
      if (restaurantId) {
        items = items.filter(i => i.restaurantId === restaurantId);
      }
      items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      resolve(items);
    };
  });
}

export async function getPendingItemsForOrder(orderId: string): Promise<PendingItem[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ITEMS_STORE, 'readonly');
    const store = transaction.objectStore(PENDING_ITEMS_STORE);
    const request = store.getAll();

    request.onerror = () => reject(new Error('Failed to get pending items for order'));
    request.onsuccess = () => {
      const items = (request.result as PendingItem[])
        .filter(i => i.orderId === orderId && i.syncStatus !== 'syncing');
      resolve(items);
    };
  });
}

export async function updatePendingItemStatus(
  id: string, 
  status: PendingItem['syncStatus'], 
  error?: string
): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ITEMS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ITEMS_STORE);
    const getRequest = store.get(id);

    getRequest.onerror = () => reject(new Error('Failed to get pending item'));
    getRequest.onsuccess = () => {
      const item = getRequest.result as PendingItem;
      if (!item) {
        reject(new Error('Item not found'));
        return;
      }

      item.syncStatus = status;
      item.syncAttempts += 1;
      if (error) {
        item.lastSyncError = error;
      }

      const putRequest = store.put(item);
      putRequest.onerror = () => reject(new Error('Failed to update pending item'));
      putRequest.onsuccess = () => resolve();
    };
  });
}

export async function deletePendingItem(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_ITEMS_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_ITEMS_STORE);
    const request = store.delete(id);

    request.onerror = () => reject(new Error('Failed to delete pending item'));
    request.onsuccess = () => resolve();
  });
}

export async function getPendingItemsCount(restaurantId: string): Promise<number> {
  const items = await getPendingItems(restaurantId);
  return items.filter(i => i.syncStatus !== 'syncing').length;
}

// ============ Pending Closures Functions (NEW) ============

export async function savePendingClosure(closure: Omit<PendingClosure, 'id' | 'createdAt' | 'syncStatus' | 'syncAttempts'>): Promise<string> {
  const database = await openDatabase();
  
  const pendingClosure: PendingClosure = {
    ...closure,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
    syncAttempts: 0,
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_CLOSURES_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_CLOSURES_STORE);
    const request = store.add(pendingClosure);

    request.onerror = () => reject(new Error('Failed to save pending closure'));
    request.onsuccess = () => resolve(pendingClosure.id);
  });
}

export async function getPendingClosures(restaurantId?: string): Promise<PendingClosure[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_CLOSURES_STORE, 'readonly');
    const store = transaction.objectStore(PENDING_CLOSURES_STORE);
    const request = store.getAll();

    request.onerror = () => reject(new Error('Failed to get pending closures'));
    request.onsuccess = () => {
      let closures = request.result as PendingClosure[];
      if (restaurantId) {
        closures = closures.filter(c => c.restaurantId === restaurantId);
      }
      closures.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      resolve(closures);
    };
  });
}

export async function getPendingClosureForOrder(orderId: string): Promise<PendingClosure | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_CLOSURES_STORE, 'readonly');
    const store = transaction.objectStore(PENDING_CLOSURES_STORE);
    const index = store.index('orderId');
    const request = index.get(orderId);

    request.onerror = () => reject(new Error('Failed to get pending closure'));
    request.onsuccess = () => {
      resolve(request.result || null);
    };
  });
}

export async function updatePendingClosureStatus(
  id: string, 
  status: PendingClosure['syncStatus'], 
  error?: string
): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_CLOSURES_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_CLOSURES_STORE);
    const getRequest = store.get(id);

    getRequest.onerror = () => reject(new Error('Failed to get pending closure'));
    getRequest.onsuccess = () => {
      const closure = getRequest.result as PendingClosure;
      if (!closure) {
        reject(new Error('Closure not found'));
        return;
      }

      closure.syncStatus = status;
      closure.syncAttempts += 1;
      if (error) {
        closure.lastSyncError = error;
      }

      const putRequest = store.put(closure);
      putRequest.onerror = () => reject(new Error('Failed to update pending closure'));
      putRequest.onsuccess = () => resolve();
    };
  });
}

export async function deletePendingClosure(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_CLOSURES_STORE, 'readwrite');
    const store = transaction.objectStore(PENDING_CLOSURES_STORE);
    const request = store.delete(id);

    request.onerror = () => reject(new Error('Failed to delete pending closure'));
    request.onsuccess = () => resolve();
  });
}

export async function getPendingClosuresCount(restaurantId: string): Promise<number> {
  const closures = await getPendingClosures(restaurantId);
  return closures.filter(c => c.syncStatus !== 'syncing').length;
}

// ============ Cached Data Functions (Existing) ============

export async function setCachedData(key: string, data: unknown): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHED_DATA_STORE, 'readwrite');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.put({
      key,
      data,
      timestamp: Date.now(),
    } as CachedData);

    request.onerror = () => reject(new Error('Failed to cache data'));
    request.onsuccess = () => resolve();
  });
}

export async function getCachedData<T>(key: string, maxAge?: number): Promise<T | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHED_DATA_STORE, 'readonly');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.get(key);

    request.onerror = () => reject(new Error('Failed to get cached data'));
    request.onsuccess = () => {
      const result = request.result as CachedData | undefined;
      if (!result) {
        resolve(null);
        return;
      }

      // Check if cache is expired
      if (maxAge && Date.now() - result.timestamp > maxAge) {
        resolve(null);
        return;
      }

      resolve(result.data as T);
    };
  });
}

export async function clearCachedData(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHED_DATA_STORE, 'readwrite');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.clear();

    request.onerror = () => reject(new Error('Failed to clear cached data'));
    request.onsuccess = () => resolve();
  });
}

// Get cache timestamp for a specific key
export async function getCacheTimestamp(key: string): Promise<number | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHED_DATA_STORE, 'readonly');
    const store = transaction.objectStore(CACHED_DATA_STORE);
    const request = store.get(key);

    request.onerror = () => reject(new Error('Failed to get cache timestamp'));
    request.onsuccess = () => {
      const result = request.result as CachedData | undefined;
      resolve(result?.timestamp ?? null);
    };
  });
}

// ============ Utility Functions ============

export async function getTotalPendingCount(restaurantId: string): Promise<{
  orders: number;
  items: number;
  closures: number;
  total: number;
}> {
  const [orders, items, closures] = await Promise.all([
    getPendingOrderCount(restaurantId),
    getPendingItemsCount(restaurantId),
    getPendingClosuresCount(restaurantId),
  ]);
  
  return {
    orders,
    items,
    closures,
    total: orders + items + closures,
  };
}

/**
 * Check if a table/command has pending operations that haven't been synced yet.
 * Used to protect local state from being overwritten by stale server data.
 */
export async function hasPendingOperationsForTable(tableId: string): Promise<boolean> {
  try {
    const [orders, items, closures] = await Promise.all([
      getPendingOrders(),
      getPendingItems(),
      getPendingClosures(),
    ]);
    
    const hasPendingOrder = orders.some(o => o.tableId === tableId);
    const hasPendingItem = items.some(i => i.tableId === tableId);
    const hasPendingClosure = closures.some(c => c.tableId === tableId);
    
    return hasPendingOrder || hasPendingItem || hasPendingClosure;
  } catch (error) {
    console.error('[hasPendingOperationsForTable] Error checking pending operations:', error);
    return false;
  }
}

/**
 * Check if a table was closed offline (has a pending closure).
 * Used during sync to avoid re-occupying a table that was already closed.
 */
export async function wasTableClosedOffline(tableId: string, afterTimestamp?: string): Promise<boolean> {
  try {
    const closures = await getPendingClosures();
    
    if (afterTimestamp) {
      return closures.some(c => 
        c.tableId === tableId && 
        new Date(c.createdAt) > new Date(afterTimestamp)
      );
    }
    
    return closures.some(c => c.tableId === tableId);
  } catch (error) {
    console.error('[wasTableClosedOffline] Error checking closures:', error);
    return false;
  }
}

export async function clearAllPendingData(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(
      [PENDING_ORDERS_STORE, PENDING_ITEMS_STORE, PENDING_CLOSURES_STORE],
      'readwrite'
    );
    
    transaction.onerror = () => reject(new Error('Failed to clear pending data'));
    transaction.oncomplete = () => resolve();

    transaction.objectStore(PENDING_ORDERS_STORE).clear();
    transaction.objectStore(PENDING_ITEMS_STORE).clear();
    transaction.objectStore(PENDING_CLOSURES_STORE).clear();
  });
}
